import { useState, useMemo, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  LIST_AUTO_RESPONDERS,
  CREATE_AUTO_RESPONDER,
  UPDATE_AUTO_RESPONDER,
  DELETE_AUTO_RESPONDER,
  GET_TENANT_AUTO_RESPONDER_ANALYTICS,
  GET_AUTO_RESPONDER_ANALYTICS,
} from "../../services/graphql/auto-responder-operations";
import { useAuthStore } from "../../stores/auth-store";
import { useGlobalClient } from "../../hooks/use-global-client";
import { CharacterLimitInput } from "../../components/auto-responder/character-limit-input";
import { AutoResponderSearchFilters } from "../../components/auto-responder/auto-responder-search-filters";
import { ConfirmationDialog } from "../../components/ui/confirmation-dialog";
import { useAutoResponderSearch } from "../../hooks/use-auto-responder-search";
import type {
  AutoResponder,
  CreateAutoResponderInput,
  UpdateAutoResponderInput,
  TenantAutoResponderAnalytics,
  AutoResponderAnalytics,
} from "../../types/auto-responder";
import type { AutoResponderFilters } from "../../hooks/use-auto-responder-search";

export function AutoRespondersPage() {
  // Get auth state for skip conditions
  const { user, isAuthenticated } = useAuthStore();

  // Get global client selection (handles both super admin and regular users)
  const {
    selectedClient,
    isSuperAdmin,
    loading: clientLoading,
    error: clientError,
  } = useGlobalClient();

  // Get tenant ID from selected client or user
  const tenantId = selectedClient?.id || user?.tenantId;

  const [messageText, setMessageText] = useState("");
  const [messageName, setMessageName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [inlineEditingId, setInlineEditingId] = useState<string | null>(null);
  const [inlineEditData, setInlineEditData] = useState<{
    name: string;
    content: string;
    isActive: boolean;
  } | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    autoResponder: AutoResponder | null;
  }>({ isOpen: false, autoResponder: null });

  // Prevent multiple rapid operations
  const operationInProgress = useRef(false);
  const mutationCallCount = useRef(0);
  const lastCreateAttempt = useRef<string | null>(null);
  const lastDeleteAttempt = useRef<string | null>(null);

  const maxLength = 130; // Auto-responder limit to leave room for destination link

  // Enhanced search and filtering
  const {
    autoResponders,
    loading,
    error,
    filters,
    setFilters,
    availableTags,
    totalCount,
    hasNextPage,
    refetch,
    loadMore,
  } = useAutoResponderSearch({
    limit: 50,
    enableRealTimeSearch: true,
  });

  // Memoize date calculations to prevent infinite re-renders
  const analyticsDateRange = useMemo(
    () => ({
      dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // Last 30 days
      dateTo: new Date().toISOString(),
    }),
    []
  );

  const { data: analyticsData } = useQuery(
    GET_TENANT_AUTO_RESPONDER_ANALYTICS,
    {
      variables: analyticsDateRange,
      skip: !isAuthenticated || !tenantId,
    }
  );

  // Individual auto-responder analytics queries
  const [selectedAnalyticsId, setSelectedAnalyticsId] = useState<string | null>(
    null
  );
  const { data: individualAnalyticsData } = useQuery(
    GET_AUTO_RESPONDER_ANALYTICS,
    {
      variables: {
        autoResponderId: selectedAnalyticsId,
        ...analyticsDateRange,
      },
      skip: !selectedAnalyticsId || !isAuthenticated || !tenantId,
    }
  );

  const [createAutoResponder, { loading: createLoading }] = useMutation(
    CREATE_AUTO_RESPONDER,
    {
      // Prevent duplicate requests with errorPolicy
      errorPolicy: "all",
      // Disable automatic retries
      notifyOnNetworkStatusChange: false,
      onCompleted: (data) => {
        mutationCallCount.current++;
        console.log(
          `Create mutation completed (call #${mutationCallCount.current}):`,
          data
        );
        operationInProgress.current = false;
        lastCreateAttempt.current = null;

        // Only clear form if creation was successful
        if (data?.createAutoResponder) {
          setMessageText("");
          setMessageName("");
          setIsActive(true);

          // Refetch to ensure UI shows all auto-responders
          setTimeout(() => {
            console.log("Calling refetch after successful creation");
            refetch();
          }, 100);
        }
      },
      onError: (error) => {
        mutationCallCount.current++;
        console.log(
          `Create mutation error (call #${mutationCallCount.current}):`,
          error
        );
        operationInProgress.current = false;
        lastCreateAttempt.current = null;
        console.error("Error creating auto-responder:", error);

        // Temporary workaround: Clear form even on error if it's a "name already exists" error
        // This allows continued testing while we debug the orphaned records issue
        if (error.message && error.message.includes("already exists")) {
          console.log(
            "Clearing form after 'already exists' error for continued testing"
          );
          setMessageText("");
          setMessageName("");
          setIsActive(true);
        }

        // Refresh the list to show current state
        console.log(
          "Refreshing list after error to show current auto-responders"
        );
        refetch();
      },
      // Add update function to properly update the cache
      update: (cache, { data }) => {
        if (data?.createAutoResponder) {
          // Update the LIST_AUTO_RESPONDERS query cache
          const existingData = cache.readQuery<{
            listAutoResponders?: { items: any[]; totalCount: number };
          }>({
            query: LIST_AUTO_RESPONDERS,
            variables: { filter: {}, limit: 50 },
          });

          if (existingData?.listAutoResponders) {
            cache.writeQuery({
              query: LIST_AUTO_RESPONDERS,
              variables: { filter: {}, limit: 50 },
              data: {
                listAutoResponders: {
                  ...existingData.listAutoResponders,
                  items: [
                    data.createAutoResponder,
                    ...existingData.listAutoResponders.items,
                  ],
                  totalCount: existingData.listAutoResponders.totalCount + 1,
                },
              },
            });
          }
        }
      },
    }
  );

  const [updateAutoResponder, { loading: updateLoading }] = useMutation(
    UPDATE_AUTO_RESPONDER,
    {
      onCompleted: () => {
        setEditingId(null);
        setMessageText("");
        setMessageName("");
        setIsActive(true);
        // Remove duplicate refetch - Apollo Client will update cache automatically
      },
      onError: (error) => {
        console.error("Error updating auto-responder:", error);
        // Error rollback is handled automatically by Apollo Client
        // The cache will revert to the previous state
      },
      // Remove optimistic response to prevent double updates
      // Apollo Client will handle the cache update when the mutation completes
    }
  );

  const [deleteAutoResponder, { loading: deleteLoading }] = useMutation(
    DELETE_AUTO_RESPONDER,
    {
      onCompleted: (data) => {
        console.log("Delete mutation completed:", data);
        operationInProgress.current = false;
        lastDeleteAttempt.current = null;
        setDeleteConfirmation({ isOpen: false, autoResponder: null });
        // Remove delayed refetch - Apollo Client will update cache automatically
      },
      onError: (error) => {
        console.log("Delete mutation error:", error);
        operationInProgress.current = false;
        lastDeleteAttempt.current = null;
        console.error("Error deleting auto-responder:", error);
        setDeleteConfirmation({ isOpen: false, autoResponder: null });

        // If the error is "not found" or "access denied", it might have been already deleted
        // Refresh the list to sync the UI with the database
        if (
          error.message &&
          (error.message.includes("not found") ||
            error.message.includes("access denied"))
        ) {
          console.log(
            "Auto-responder not found or access denied - refreshing list to sync UI"
          );
          console.log(
            "This usually means the item was already deleted or there's a cache sync issue"
          );
          setTimeout(() => {
            refetch();
          }, 100);
        }
      },
      // Add update function to properly remove from cache
      update: (cache, { data }, { variables }) => {
        if (data && variables?.id) {
          // Remove from LIST_AUTO_RESPONDERS query cache
          const existingData = cache.readQuery<{
            listAutoResponders?: { items: any[]; totalCount: number };
          }>({
            query: LIST_AUTO_RESPONDERS,
            variables: { filter: {}, limit: 50 },
          });

          if (existingData?.listAutoResponders) {
            cache.writeQuery({
              query: LIST_AUTO_RESPONDERS,
              variables: { filter: {}, limit: 50 },
              data: {
                listAutoResponders: {
                  ...existingData.listAutoResponders,
                  items: existingData.listAutoResponders.items.filter(
                    (item: any) => item.id !== variables.id
                  ),
                  totalCount: Math.max(
                    0,
                    existingData.listAutoResponders.totalCount - 1
                  ),
                },
              },
            });
          }
        }
      },
    }
  );

  const analytics =
    analyticsData?.getTenantAutoResponderAnalytics as TenantAutoResponderAnalytics;

  const handleCreate = useCallback(async () => {
    console.log("handleCreate called - checking conditions");
    console.log("messageName:", messageName.trim());
    console.log("messageText:", messageText.trim());
    console.log("createLoading:", createLoading);
    console.log("operationInProgress.current:", operationInProgress.current);

    // Create a unique identifier for this creation attempt
    const attemptId = `${messageName.trim()}-${messageText.trim()}-${Date.now()}`;

    // Check for duplicate attempts
    if (lastCreateAttempt.current === attemptId) {
      console.log("handleCreate - duplicate attempt detected, ignoring");
      return;
    }

    if (
      !messageName.trim() ||
      !messageText.trim() ||
      createLoading ||
      operationInProgress.current
    ) {
      console.log("handleCreate - early return due to conditions");
      return;
    }

    console.log("handleCreate - setting operation in progress");
    operationInProgress.current = true;
    lastCreateAttempt.current = attemptId;

    const input: CreateAutoResponderInput = {
      name: messageName.trim(),
      content: messageText.trim(),
      isActive,
      deviceActions: {
        mobileAction: "SMS",
        desktopAction: "CHAT",
      },
    };

    try {
      console.log("Calling createAutoResponder with input:", input);
      const result = await createAutoResponder({ variables: { input } });
      console.log("createAutoResponder completed:", result);
    } catch (error) {
      console.error("Error creating auto-responder:", error);
      // Reset the attempt tracking on error so user can retry
      lastCreateAttempt.current = null;
    } finally {
      console.log("handleCreate - clearing operation in progress");
      operationInProgress.current = false;
    }
  }, [messageName, messageText, createLoading, isActive, createAutoResponder]);

  const handleEdit = (autoResponder: AutoResponder) => {
    setEditingId(autoResponder.id);
    setMessageName(autoResponder.name);
    setMessageText(autoResponder.content);
    setIsActive(autoResponder.isActive);
  };

  const handleInlineEdit = (autoResponder: AutoResponder) => {
    setInlineEditingId(autoResponder.id);
    setInlineEditData({
      name: autoResponder.name,
      content: autoResponder.content,
      isActive: autoResponder.isActive,
    });
  };

  const handleInlineUpdate = async (autoResponderId: string) => {
    if (
      !inlineEditData ||
      !inlineEditData.name.trim() ||
      !inlineEditData.content.trim()
    )
      return;

    const input: UpdateAutoResponderInput = {
      id: autoResponderId,
      name: inlineEditData.name.trim(),
      content: inlineEditData.content.trim(),
      isActive: inlineEditData.isActive,
    };

    try {
      await updateAutoResponder({ variables: { input } });
      setInlineEditingId(null);
      setInlineEditData(null);
    } catch (error) {
      console.error("Error updating auto-responder:", error);
      // Apollo Client automatically handles optimistic update rollback on error
      // The UI will revert to the previous state automatically
      // We could add a toast notification here for better UX
    }
  };

  const handleInlineCancelEdit = () => {
    setInlineEditingId(null);
    setInlineEditData(null);
  };

  const handleUpdate = async () => {
    if (!editingId || !messageName.trim() || !messageText.trim()) return;

    const input: UpdateAutoResponderInput = {
      id: editingId,
      name: messageName.trim(),
      content: messageText.trim(),
      isActive,
    };

    await updateAutoResponder({ variables: { input } });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setMessageText("");
    setMessageName("");
    setIsActive(true);
  };

  const handleDeleteClick = (autoResponder: AutoResponder) => {
    console.log("handleDeleteClick called with autoResponder:", autoResponder);
    setDeleteConfirmation({ isOpen: true, autoResponder });
  };

  const handleDeleteConfirm = async () => {
    console.log("handleDeleteConfirm called");
    console.log(
      "deleteConfirmation.autoResponder:",
      deleteConfirmation.autoResponder
    );
    console.log("deleteLoading:", deleteLoading);
    console.log("operationInProgress.current:", operationInProgress.current);

    // Create a unique identifier for this delete attempt
    const deleteAttemptId =
      deleteConfirmation.autoResponder?.id + "-" + Date.now();

    // Check for duplicate delete attempts
    if (lastDeleteAttempt.current === deleteAttemptId) {
      console.log(
        "handleDeleteConfirm - duplicate delete attempt detected, ignoring"
      );
      return;
    }

    if (
      !deleteConfirmation.autoResponder ||
      deleteLoading ||
      operationInProgress.current
    ) {
      console.log("handleDeleteConfirm - early return due to conditions");
      return;
    }

    lastDeleteAttempt.current = deleteAttemptId;

    // Check if the item still exists in the current list
    const itemExists = autoResponders.find(
      (ar) => ar.id === deleteConfirmation.autoResponder?.id
    );
    console.log("Item exists in current list:", !!itemExists);
    console.log("Current autoResponders count:", autoResponders.length);

    if (!itemExists) {
      console.log(
        "Item no longer exists in list, closing dialog and refreshing"
      );
      setDeleteConfirmation({ isOpen: false, autoResponder: null });
      refetch();
      return;
    }

    console.log("handleDeleteConfirm - setting operation in progress");
    operationInProgress.current = true;

    try {
      console.log(
        "Calling deleteAutoResponder with id:",
        deleteConfirmation.autoResponder.id
      );
      console.log("Auto-responder details:", {
        id: deleteConfirmation.autoResponder.id,
        name: deleteConfirmation.autoResponder.name,
        tenantId: deleteConfirmation.autoResponder.tenantId,
      });

      const result = await deleteAutoResponder({
        variables: { id: deleteConfirmation.autoResponder.id },
      });
      console.log("deleteAutoResponder completed:", result);
    } catch (error) {
      console.error("Error deleting auto-responder:", error);

      // If the error is "not found", show a user-friendly message
      if (error.message && error.message.includes("not found")) {
        console.log(
          "Auto-responder was already deleted - this is likely a UI sync issue"
        );
        // The onError handler will refresh the list
      }
      // Error is already handled in the mutation's onError callback
    } finally {
      console.log("handleDeleteConfirm - clearing operation in progress");
      operationInProgress.current = false;
      lastDeleteAttempt.current = null;
    }
  };

  const getDeviceActionIcon = (action: "SMS" | "CHAT" | "REDIRECT") => {
    switch (action) {
      case "SMS":
        return "📱";
      case "CHAT":
        return "💬";
      case "REDIRECT":
        return "🔗";
      default:
        return "❓";
    }
  };

  const getDeviceActionLabel = (action: "SMS" | "CHAT" | "REDIRECT") => {
    switch (action) {
      case "SMS":
        return "SMS App";
      case "CHAT":
        return "Chat Interface";
      case "REDIRECT":
        return "URL Redirect";
      default:
        return "Unknown";
    }
  };

  const getIndividualAnalytics = (
    autoResponderId: string
  ): AutoResponderAnalytics | null => {
    if (selectedAnalyticsId === autoResponderId && individualAnalyticsData) {
      return individualAnalyticsData.getAutoResponderAnalytics;
    }
    return null;
  };

  const toggleAnalytics = (autoResponderId: string) => {
    if (selectedAnalyticsId === autoResponderId) {
      setSelectedAnalyticsId(null);
    } else {
      setSelectedAnalyticsId(autoResponderId);
    }
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  // Show client selection prompt for super admins who haven't selected a client
  if (isSuperAdmin && !selectedClient) {
    return (
      <div className="space-y-6" data-testid="auto-responders-page">
        <div className="border-b border-gray-200 pb-4">
          <h1 className="text-3xl font-bold text-gray-900">Auto-Responders</h1>
          <p className="mt-2 text-gray-600">
            Reusable message components with SMS/chat routing and 160-character
            validation.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Select a Client
          </h3>
          <p className="text-gray-600 mb-4">
            Please select a client from the dropdown in the header to view and
            manage auto-responders.
          </p>
          <p className="text-sm text-gray-500">
            As a super admin, you can switch between different clients using the
            client selector in the top navigation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="auto-responders-page">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Auto-Responders
            </h1>
            <p className="mt-2 text-gray-600">
              Reusable message components with SMS/chat routing and
              160-character validation.
            </p>
            {isSuperAdmin && selectedClient && (
              <p className="mt-1 text-sm text-blue-600">
                Managing auto-responders for:{" "}
                <span className="font-medium">{selectedClient.clientName}</span>
              </p>
            )}
          </div>
          <button
            onClick={() => {
              console.log("Manual refresh clicked");
              refetch();
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh List"}
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      {/* <div
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
        data-testid="analytics-summary"
      >
        <div
          className="bg-white p-6 rounded-lg shadow border"
          data-testid="stats-active-messages"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Active Messages
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading
                  ? "..."
                  : analytics?.totalAutoResponders ||
                    autoResponders.filter((ar) => ar.isActive).length}
              </p>
            </div>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-lg shadow border"
          data-testid="stats-sms-sent"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">SMS Sent</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : analytics?.smsUsage?.toLocaleString() || "0"}
              </p>
            </div>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-lg shadow border"
          data-testid="stats-chat-messages"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Chat Messages</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading
                  ? "..."
                  : analytics?.chatUsage?.toLocaleString() || "0"}
              </p>
            </div>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-lg shadow border"
          data-testid="stats-response-rate"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Response Rate</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading
                  ? "..."
                  : analytics?.averageResponseRate
                  ? `${analytics.averageResponseRate.toFixed(1)}%`
                  : "0%"}
              </p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Device Breakdown Analytics */}
      {/* {analytics?.deviceBreakdown && (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Device Usage Breakdown (Last 30 Days)
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <span className="text-2xl">📱</span>
                </div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  Phone
                </h3>
                <p className="text-2xl font-semibold text-blue-600">
                  {formatNumber(analytics.deviceBreakdown.PHONE)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.totalUsage > 0
                    ? `${(
                        (analytics.deviceBreakdown.PHONE /
                          analytics.totalUsage) *
                        100
                      ).toFixed(1)}%`
                    : "0%"}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <span className="text-2xl">💻</span>
                </div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  Desktop
                </h3>
                <p className="text-2xl font-semibold text-green-600">
                  {formatNumber(analytics.deviceBreakdown.DESKTOP)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.totalUsage > 0
                    ? `${(
                        (analytics.deviceBreakdown.DESKTOP /
                          analytics.totalUsage) *
                        100
                      ).toFixed(1)}%`
                    : "0%"}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <span className="text-2xl">📱</span>
                </div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  Tablet
                </h3>
                <p className="text-2xl font-semibold text-purple-600">
                  {formatNumber(analytics.deviceBreakdown.TABLET)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.totalUsage > 0
                    ? `${(
                        (analytics.deviceBreakdown.TABLET /
                          analytics.totalUsage) *
                        100
                      ).toFixed(1)}%`
                    : "0%"}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <span className="text-2xl">❓</span>
                </div>
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  Other
                </h3>
                <p className="text-2xl font-semibold text-gray-600">
                  {formatNumber(analytics.deviceBreakdown.OTHER)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.totalUsage > 0
                    ? `${(
                        (analytics.deviceBreakdown.OTHER /
                          analytics.totalUsage) *
                        100
                      ).toFixed(1)}%`
                    : "0%"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Top Performing Auto-Responders */}
      {/* {analytics?.topAutoResponders &&
        analytics.topAutoResponders.length > 0 && (
          <div className="bg-white rounded-lg shadow border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Top Performing Auto-Responders (Last 30 Days)
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {analytics.topAutoResponders
                  .slice(0, 5)
                  .map((topResponder, index) => (
                    <div
                      key={topResponder.autoResponderId}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {topResponder.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {formatNumber(topResponder.usage)} uses
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatPercentage(topResponder.responseRate)} response
                          rate
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )} */}

      {/* Create/Edit Auto-Responder */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {editingId ? "Edit Auto-Responder" : "Create New Auto-Responder"}
          </h2>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div>
              <label
                htmlFor="message-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Message Name
              </label>
              <input
                type="text"
                id="message-name"
                value={messageName}
                onChange={(e) => setMessageName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Welcome Message, Product Demo"
                disabled={createLoading || updateLoading}
              />
            </div>

            <CharacterLimitInput
              value={messageText}
              onChange={setMessageText}
              maxLength={maxLength}
              label="Message Content"
              placeholder="Enter your auto-responder message (max 130 characters to leave room for destination link)"
              disabled={createLoading || updateLoading}
            />

            {/* <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={createLoading || updateLoading}
              />
              <label htmlFor="enabled" className="text-sm text-gray-700">
                Enable this auto-responder
              </label>
            </div> */}

            <div className="flex justify-end space-x-3">
              {editingId && (
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={createLoading || updateLoading}
                >
                  Cancel
                </button>
              )}
              <button
                onClick={editingId ? handleUpdate : handleCreate}
                disabled={
                  !messageName.trim() ||
                  !messageText.trim() ||
                  messageText.length > maxLength ||
                  createLoading ||
                  updateLoading ||
                  operationInProgress.current
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="create-auto-responder-button"
              >
                {createLoading || updateLoading ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {editingId ? "Updating..." : "Creating..."}
                  </div>
                ) : editingId ? (
                  "Update Auto-Responder"
                ) : (
                  "Create Auto-Responder"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Search & Filter Auto-Responders
          </h2>
        </div>
        <div className="p-6">
          <AutoResponderSearchFilters
            filters={filters}
            onFiltersChange={setFilters}
            availableTags={availableTags}
            loading={loading}
          />
        </div>
      </div>

      {/* Existing Auto-Responders */}
      <div
        className="bg-white rounded-lg shadow border"
        data-testid="auto-responders-list"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Auto-Responders
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({autoResponders.length} loaded, {totalCount} total)
              </span>
            </h2>
            {hasNextPage && (
              <button
                onClick={loadMore}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            )}
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading auto-responders...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading auto-responders
              </h3>
              <p className="text-gray-500 mb-4">{error.message}</p>
              <button
                onClick={() => refetch()}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {autoResponders.length === 0 && !loading ? (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No auto-responders found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {Object.keys(filters).length > 0
                      ? "Try adjusting your search filters or create a new auto-responder."
                      : "Get started by creating your first auto-responder message."}
                  </p>
                  {Object.keys(filters).length > 0 && (
                    <button
                      onClick={() => setFilters({})}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              ) : (
                autoResponders.map((responder) => (
                  <div
                    key={responder.id}
                    className="p-6 hover:bg-gray-50 transition-colors"
                  >
                    {inlineEditingId === responder.id && inlineEditData ? (
                      // Inline Edit Form
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Message Name
                          </label>
                          <input
                            type="text"
                            value={inlineEditData.name}
                            onChange={(e) =>
                              setInlineEditData({
                                ...inlineEditData,
                                name: e.target.value,
                              })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Welcome Message, Product Demo"
                            disabled={updateLoading}
                          />
                        </div>

                        <div>
                          <CharacterLimitInput
                            value={inlineEditData.content}
                            onChange={(value) =>
                              setInlineEditData({
                                ...inlineEditData,
                                content: value,
                              })
                            }
                            maxLength={maxLength}
                            label="Message Content"
                            placeholder="Enter your auto-responder message (max 130 characters to leave room for destination link)"
                            disabled={updateLoading}
                          />
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`inline-enabled-${responder.id}`}
                            checked={inlineEditData.isActive}
                            onChange={(e) =>
                              setInlineEditData({
                                ...inlineEditData,
                                isActive: e.target.checked,
                              })
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            disabled={updateLoading}
                          />
                          <label
                            htmlFor={`inline-enabled-${responder.id}`}
                            className="text-sm text-gray-700"
                          >
                            Enable this auto-responder
                          </label>
                        </div>

                        <div className="flex justify-end space-x-3 pt-2">
                          <button
                            onClick={handleInlineCancelEdit}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            disabled={updateLoading}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleInlineUpdate(responder.id)}
                            disabled={
                              !inlineEditData.name.trim() ||
                              !inlineEditData.content.trim() ||
                              inlineEditData.content.length > maxLength ||
                              updateLoading
                            }
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {updateLoading ? (
                              <div className="flex items-center">
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                Updating...
                              </div>
                            ) : (
                              "Update Auto-Responder"
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Display Mode
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-medium text-gray-900">
                                {responder.name}
                              </h3>
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  responder.isActive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {responder.isActive ? "Active" : "Inactive"}
                              </span>
                            </div>
                            <p className="text-gray-600 mb-3">
                              "{responder.content}"
                            </p>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            {/* <button
                              onClick={() => toggleAnalytics(responder.id)}
                              className="inline-flex items-center px-3 py-1 text-sm text-purple-600 hover:text-purple-700 font-medium"
                              data-testid={`analytics-button-${responder.id}`}
                            >
                              <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                />
                              </svg>
                              {selectedAnalyticsId === responder.id
                                ? "Hide"
                                : "Analytics"}
                            </button> */}
                            {/* <button
                              onClick={() => handleInlineEdit(responder)}
                              className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                              disabled={
                                inlineEditingId !== null || updateLoading
                              }
                              data-testid={`edit-button-${responder.id}`}
                            >
                              <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                              Edit
                            </button> */}
                            <button
                              onClick={() => handleEdit(responder)}
                              className="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-700 font-medium"
                              disabled={
                                editingId === responder.id ||
                                inlineEditingId !== null
                              }
                              title="Edit in form above"
                            >
                              <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3 3-3M12 12l0 9"
                                />
                              </svg>
                              Edit
                            </button>

                            <button
                              onClick={() => handleDeleteClick(responder)}
                              className="inline-flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-700 font-medium"
                              disabled={
                                deleteLoading || inlineEditingId !== null
                              }
                              data-testid={`delete-button-${responder.id}`}
                            >
                              <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </div>

                        {/* Individual Analytics Panel */}
                        {selectedAnalyticsId === responder.id && (
                          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-500">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">
                              Performance Analytics (Last 30 Days)
                            </h4>

                            {individualAnalyticsData?.getAutoResponderAnalytics ? (
                              <div className="space-y-4">
                                {/* Usage Metrics */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <div className="bg-white p-3 rounded-lg border">
                                    <div className="text-xs text-gray-500 mb-1">
                                      Total Usage
                                    </div>
                                    <div className="text-lg font-semibold text-gray-900">
                                      {formatNumber(
                                        individualAnalyticsData
                                          .getAutoResponderAnalytics.totalUsage
                                      )}
                                    </div>
                                  </div>
                                  <div className="bg-white p-3 rounded-lg border">
                                    <div className="text-xs text-gray-500 mb-1">
                                      SMS Sent
                                    </div>
                                    <div className="text-lg font-semibold text-green-600">
                                      {formatNumber(
                                        individualAnalyticsData
                                          .getAutoResponderAnalytics.smsUsage
                                      )}
                                    </div>
                                  </div>
                                  <div className="bg-white p-3 rounded-lg border">
                                    <div className="text-xs text-gray-500 mb-1">
                                      Chat Messages
                                    </div>
                                    <div className="text-lg font-semibold text-blue-600">
                                      {formatNumber(
                                        individualAnalyticsData
                                          .getAutoResponderAnalytics.chatUsage
                                      )}
                                    </div>
                                  </div>
                                  <div className="bg-white p-3 rounded-lg border">
                                    <div className="text-xs text-gray-500 mb-1">
                                      Response Rate
                                    </div>
                                    <div className="text-lg font-semibold text-orange-600">
                                      {formatPercentage(
                                        individualAnalyticsData
                                          .getAutoResponderAnalytics
                                          .responseRate
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Device Breakdown */}
                                <div className="bg-white p-4 rounded-lg border">
                                  <h5 className="text-sm font-medium text-gray-900 mb-3">
                                    Device Breakdown
                                  </h5>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="text-center">
                                      <div className="text-2xl mb-1">📱</div>
                                      <div className="text-sm text-gray-500">
                                        Phone
                                      </div>
                                      <div className="text-lg font-semibold">
                                        {formatNumber(
                                          individualAnalyticsData
                                            .getAutoResponderAnalytics
                                            .deviceBreakdown.PHONE
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl mb-1">💻</div>
                                      <div className="text-sm text-gray-500">
                                        Desktop
                                      </div>
                                      <div className="text-lg font-semibold">
                                        {formatNumber(
                                          individualAnalyticsData
                                            .getAutoResponderAnalytics
                                            .deviceBreakdown.DESKTOP
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl mb-1">📱</div>
                                      <div className="text-sm text-gray-500">
                                        Tablet
                                      </div>
                                      <div className="text-lg font-semibold">
                                        {formatNumber(
                                          individualAnalyticsData
                                            .getAutoResponderAnalytics
                                            .deviceBreakdown.TABLET
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl mb-1">❓</div>
                                      <div className="text-sm text-gray-500">
                                        Other
                                      </div>
                                      <div className="text-lg font-semibold">
                                        {formatNumber(
                                          individualAnalyticsData
                                            .getAutoResponderAnalytics
                                            .deviceBreakdown.OTHER
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Conversation Breakdown */}
                                {individualAnalyticsData
                                  .getAutoResponderAnalytics
                                  .conversationBreakdown.length > 0 && (
                                  <div className="bg-white p-4 rounded-lg border">
                                    <h5 className="text-sm font-medium text-gray-900 mb-3">
                                      Usage by Conversation
                                    </h5>
                                    <div className="space-y-2">
                                      {individualAnalyticsData.getAutoResponderAnalytics.conversationBreakdown
                                        .slice(0, 5)
                                        .map((conv, index) => (
                                          <div
                                            key={conv.conversationId}
                                            className="flex justify-between items-center"
                                          >
                                            <span className="text-sm text-gray-600">
                                              Conversation{" "}
                                              {conv.conversationId.slice(-8)}
                                            </span>
                                            <span className="text-sm font-medium">
                                              {formatNumber(conv.usage)} uses
                                            </span>
                                          </div>
                                        ))}
                                      {individualAnalyticsData
                                        .getAutoResponderAnalytics
                                        .conversationBreakdown.length > 5 && (
                                        <div className="text-xs text-gray-500 text-center pt-2">
                                          +
                                          {individualAnalyticsData
                                            .getAutoResponderAnalytics
                                            .conversationBreakdown.length -
                                            5}{" "}
                                          more conversations
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Recent Activity */}
                                {individualAnalyticsData
                                  .getAutoResponderAnalytics.dailyUsage.length >
                                  0 && (
                                  <div className="bg-white p-4 rounded-lg border">
                                    <h5 className="text-sm font-medium text-gray-900 mb-3">
                                      Recent Activity (Last 7 Days)
                                    </h5>
                                    <div className="space-y-2">
                                      {individualAnalyticsData.getAutoResponderAnalytics.dailyUsage
                                        .slice(-7)
                                        .map((day) => (
                                          <div
                                            key={day.date}
                                            className="flex justify-between items-center"
                                          >
                                            <span className="text-sm text-gray-600">
                                              {new Date(
                                                day.date
                                              ).toLocaleDateString()}
                                            </span>
                                            <span className="text-sm font-medium">
                                              {formatNumber(day.usage)} uses
                                            </span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                                <p className="text-sm text-gray-500">
                                  Loading analytics...
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}

              {/* No Results Message */}
              {autoResponders.length === 0 && filters.search && (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No auto-responders found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    No auto-responders match your search for "{filters.search}"
                  </p>
                  <button
                    onClick={() => setFilters({ ...filters, search: "" })}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Clear search
                  </button>
                </div>
              )}

              {/* Empty State */}
              {autoResponders.length === 0 && !filters.search && (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No auto-responders yet
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Create your first auto-responder message to get started.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={() =>
          setDeleteConfirmation({ isOpen: false, autoResponder: null })
        }
        onConfirm={handleDeleteConfirm}
        title="Delete Auto-Responder"
        message={
          deleteConfirmation.autoResponder
            ? `Are you sure you want to delete "${
                deleteConfirmation.autoResponder.name
              }"? ${
                deleteConfirmation.autoResponder.conversationIds.length > 0
                  ? `This auto-responder is currently attached to ${deleteConfirmation.autoResponder.conversationIds.length} conversation(s) and will be removed from them.`
                  : "This action cannot be undone."
              }`
            : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
}
